import { hash } from "object-code"
import { useEffect, useMemo, useRef } from "react"
import type { ColorScheme } from "./brand"
import { Processor } from "./lib/processor"
import { DrawingRenderer } from "./lib/renderer-drawing"

type Props = {
  processor: Processor
  cellSize: number
  rows: number
  cols: number
  lineOffset: number
  background?: string
  padding: number
  scheme: ColorScheme
  drawing?: boolean
  onColorChange?(scheme: ColorScheme): void
}

/**
 * Component which renders Flowgram data as an SVG
 */
export function FlowgramDrawingRenderer({ processor, ...args }: Props) {
  const ref = useRef<HTMLCanvasElement>(null)
  const renderer = useMemo(() => new DrawingRenderer(), [processor])

  useEffect(() => {
    renderer.setCanvas(ref.current!)
  }, [renderer])

  useEffect(() => {
    renderer.setArgs({ ...args, shapes: processor.shapes, processor })
  }, [renderer, hash(args), processor])

  useEffect(() => {
    renderer.queueShapes(processor.shapes)
    return processor.onChange(() => {
      renderer.queueShapes(processor.shapes)
    })
  }, [renderer, processor])

  useEffect(() => {
    renderer.start()
    return () => renderer.stop()
  }, [renderer])

  return <canvas ref={ref} className="absolute top-0 left-0 w-full h-full flowgram-output" />
}
